"use client";

import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LoadingSpinner from "./LoadingSpinner";

// ===== helpers de roles (mismo enfoque que Header) =====
function getRawRolesFromUser(user) {
  if (!user?.roles) return [];
  return user.roles
    .map((r) => {
      if (typeof r === "string") return r;
      return r.nombre || r.rol || r.role || "";
    })
    .filter(Boolean)
    .map((r) => String(r).toLowerCase());
}

function hasCourseRole(user, roleName) {
  const target = String(roleName || "").toLowerCase();

  const courseRolesObj = user?.course_roles || user?.curso_roles || null;
  if (courseRolesObj && typeof courseRolesObj === "object" && !Array.isArray(courseRolesObj)) {
    if (courseRolesObj[target]) return true;
  }

  const teacherRolesArr = user?.teacher_roles || user?.roles_docente || null;
  if (Array.isArray(teacherRolesArr)) {
    if (teacherRolesArr.some((r) => String(r).toLowerCase() === target)) return true;
  }

  return false;
}

function getAvailableRolesFromUser(user) {
  const baseRoles = getRawRolesFromUser(user);

  // globales: administrador/estudiante/... (docente/asistente vienen por curso)
  const globalRoles = [];
  baseRoles.forEach((r) => {
    if (r === "docente" || r === "asistente") return;
    globalRoles.push(r);
  });

  const rolesSet = new Set(globalRoles);

  if (hasCourseRole(user, "docente")) rolesSet.add("docente");
  if (hasCourseRole(user, "asistente")) rolesSet.add("asistente");

  return Array.from(rolesSet);
}

const AdminRoute = ({ children }) => {
  const { user, currentRole, isAuthenticated } = useAuth();
  const location = useLocation();

  const hasSession = Boolean(isAuthenticated && user);

  // ✅ Rol efectivo igual que Header: si currentRole no está, usamos availableRoles[0]
  const availableRoles = useMemo(() => getAvailableRolesFromUser(user), [user]);

  const effectiveRole = useMemo(() => {
    const cr = String(currentRole || "").toLowerCase().trim();
    if (cr) return cr;
    if (availableRoles.length > 0) return String(availableRoles[0] || "").toLowerCase();
    return "";
  }, [currentRole, availableRoles]);

  // ✅ Mientras se hidrata, NO redirigir ni denegar: spinner.
  const roleReady = hasSession ? Boolean(effectiveRole) : true;

  // 1) Si no hay sesión, manda al login pero guardando "from"
  if (!hasSession) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // 2) Si hay sesión pero aún no hay rol calculable, espera (esto arregla el refresh)
  if (!roleReady) {
    return (
      <div style={{ padding: 40 }}>
        <LoadingSpinner message="Verificando acceso de administrador…" />
      </div>
    );
  }

  // 3) Admin check con rol efectivo
  const hasFacultyScope = Array.isArray(user?.admin_facultades) && user.admin_facultades.length > 0;

  const isAdmin =
    effectiveRole === "administrador" ||
    effectiveRole === "admin" ||
    effectiveRole === "superadmin" ||
    (effectiveRole === "admin_facultad" && hasFacultyScope);

  if (!isAdmin) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        flexDirection: "column",
        textAlign: "center",
        padding: "20px"
      }}>
        <div style={{
          background: "linear-gradient(135deg, #f8d7da, #f5c6cb)",
          border: "1px solid #f1aeb5",
          borderRadius: "12px",
          padding: "40px",
          maxWidth: "500px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)"
        }}>
          <div style={{ fontSize: "4rem", marginBottom: "20px" }}>🚫</div>
          <h1 style={{
            color: "#721c24",
            fontSize: "2rem",
            marginBottom: "16px",
            fontWeight: "700"
          }}>
            Acceso Denegado
          </h1>
          <p style={{
            color: "#721c24",
            fontSize: "1.1rem",
            marginBottom: "24px",
            lineHeight: "1.6"
          }}>
            Esta sección está reservada únicamente para administradores.
            Tu rol actual es: <strong>{effectiveRole || currentRole || "(sin rol)"}</strong>
          </p>

          <div style={{
            background: "rgba(114, 28, 36, 0.1)",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "20px"
          }}>
            <p style={{ color: "#721c24", margin: 0, fontSize: "0.9rem" }}>
              💡 Si necesitas acceso de administrador, contacta con tu supervisor o cambia tu rol desde el menú de usuario.
            </p>
          </div>

          <button
            onClick={() => window.history.back()}
            style={{
              background: "linear-gradient(135deg, #4CAF50, #388E3C)",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "25px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)"
            }}
            onMouseOver={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 20px rgba(76, 175, 80, 0.4)";
            }}
            onMouseOut={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 15px rgba(76, 175, 80, 0.3)";
            }}
          >
            ← Volver
          </button>

          <div style={{ marginTop: 18, fontSize: 12, color: "#7f1d1d" }}>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default AdminRoute;
